// src/lib/caution-phrase.js — LA phrase de caution dite au voyageur (courriels
// du parcours direct, espace séjour). Source UNIQUE, CommonJS pur : l'écran
// l'importe directement, le serveur la lit par `require("../src/lib/...")`
// (même sens que lib/caution-mode.js).
//
// ⚠️ CHEMIN ARGENT (texte). La phrase REFLÈTE le mode décidé ailleurs, elle ne
// le calcule jamais :
//   - 'empreinte' / 'debit' : le mode rendu par LA porte cautionPourSejour
//     (src/lib/caution-figee.js), le même que Stripe et le contrat ;
//   - toute autre valeur (null, undefined, '') : mode INCONNU au moment du
//     message → phrase NEUTRE, juste dans les deux cas, avec le seuil du
//     logement s'il est connu. Jamais « rien n'est prélevé » par défaut : au-delà
//     du seuil, Stripe prélève (api/stripe.js).
//
// Mots : maquette NEST v677 (lignes 21741, 26786, 26909), au mot près pour le
// français. Séjour au-delà du seuil : « débitée 2 jours avant l'arrivée, rendue
// après votre départ ». Empreinte : « rien n'est prélevé » (court), « sauf dégât
// constaté » (long). La phrase NEUTRE n'est pas dans la maquette : à valider
// par Dimitri. Anglais et espagnol : traductions fidèles de ces mots.

const { CAUTION_HOLD_MAX_NIGHTS } = require("./caution-mode.js");

const MODES = ["empreinte", "debit"];

/** Le mode s'il est connu ('empreinte' | 'debit'), sinon null. */
function modeCautionConnu(mode) {
  return MODES.includes(mode) ? mode : null;
}

function _seuil(seuil) {
  return Number.isInteger(seuil) && seuil >= 1 ? seuil : CAUTION_HOLD_MAX_NIGHTS;
}

function _nuits(n, lang) {
  if (lang === "en") return `${n} night${n > 1 ? "s" : ""}`;
  if (lang === "es") return `${n} noche${n > 1 ? "s" : ""}`;
  return `${n} nuit${n > 1 ? "s" : ""}`;
}

const PHRASES = {
  fr: {
    empreinte: () =>
      "par empreinte bancaire au profit du loueur, rien n'est prélevé sauf dégât constaté",
    debit: () => "débitée 2 jours avant l'arrivée, rendue après votre départ",
    neutre: (n) =>
      `empreinte bancaire pour un séjour de ${_nuits(n, "fr")} ou moins, sans rien prélever sauf dégât constaté ; au-delà, le montant est prélevé puis remboursé après votre départ, selon votre contrat`,
  },
  en: {
    empreinte: () =>
      "by card hold in favour of the host, nothing is charged unless damage is found",
    debit: () => "charged 2 days before arrival, returned after your departure",
    neutre: (n) =>
      `a card hold for stays of ${_nuits(n, "en")} or fewer, with nothing taken unless damage is found; for longer stays, the amount is charged then refunded after your departure, as set out in your contract`,
  },
  es: {
    empreinte: () =>
      "mediante retención bancaria a favor del arrendador, no se cobra nada salvo daños constatados",
    debit: () => "se cobra 2 días antes de la llegada y se devuelve tras su salida",
    neutre: (n) =>
      `una retención en su tarjeta para estancias de ${_nuits(n, "es")} o menos, sin cobrar nada salvo daños constatados; para estancias más largas, el importe se cobra y se reembolsa tras su salida, según su contrato`,
  },
};

/**
 * La phrase de caution (fragment en minuscule, sans point final, à placer
 * après « Caution : 300 € — » ou entre parenthèses).
 *
 * @param {'empreinte'|'debit'|null|undefined} mode — mode DÉJÀ calculé, ou inconnu
 * @param {'fr'|'en'|'es'} [lang='fr']
 * @param {number} [seuil] — seuil en nuits, si connu ; sinon le seuil commun
 * @returns {string}
 */
function phraseCaution(mode, lang = "fr", seuil) {
  const p = PHRASES[lang] || PHRASES.fr;
  const m = modeCautionConnu(mode);
  if (m) return p[m]();
  return p.neutre(_seuil(seuil));
}

// Version COURTE (une ligne de liste, après « 300 € · ») — mêmes faits, en
// minuscule comme la maquette (ligne 21741).
const COURTES = {
  fr: {
    empreinte: () => "une empreinte bancaire, rien n'est prélevé",
    debit: () => "débitée 2 jours avant l'arrivée, rendue après votre départ",
    neutre: (n) => `empreinte jusqu’à ${_nuits(n, "fr")} · au-delà, prélevée puis remboursée`,
  },
  en: {
    empreinte: () => "a card hold, nothing is charged",
    debit: () => "charged 2 days before arrival, returned after your departure",
    neutre: (n) => `card hold up to ${_nuits(n, "en")} · beyond, charged then refunded`,
  },
  es: {
    empreinte: () => "una retención bancaria, no se cobra nada",
    debit: () => "se cobra 2 días antes de la llegada y se devuelve tras su salida",
    neutre: (n) => `retención hasta ${_nuits(n, "es")} · más allá, se cobra y se reembolsa`,
  },
};

/** La phrase courte (même règle : mode connu → son texte, sinon neutre). */
function phraseCautionCourte(mode, lang = "fr", seuil) {
  const p = COURTES[lang] || COURTES.fr;
  const m = modeCautionConnu(mode);
  return m ? p[m]() : p.neutre(_seuil(seuil));
}

/** La phrase courte seule sur sa ligne, sous un bouton : majuscule en tête. */
function sousBoutonCaution(mode, lang = "fr", seuil) {
  const t = phraseCautionCourte(mode, lang, seuil);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Les textes de l'étape « Caution » de l'espace séjour (FR) : fait, en cours,
 * sous le bouton. Même règle : mode connu → son texte, sinon neutre.
 */
function libellesEtapeCaution(mode, seuil) {
  const m = modeCautionConnu(mode);
  const n = _seuil(seuil);
  const done = {
    empreinte: "Déposée · empreinte, rien n'est prélevé",
    debit: "Débitée · rendue après votre départ",
    neutre: "Caution enregistrée. Elle vous est rendue après votre départ, selon votre contrat.",
  };
  const active = {
    empreinte: "Une empreinte bancaire, rien n'est prélevé sauf dégât constaté.",
    debit: "Débitée 2 jours avant l'arrivée, rendue après votre départ.",
    neutre: `Empreinte bancaire jusqu’à ${_nuits(n, "fr")} ; au-delà, la caution est prélevée puis remboursée.`,
  };
  const k = m || "neutre";
  return { done: done[k], active: active[k], sous: sousBoutonCaution(m, "fr", n) };
}

module.exports = {
  phraseCaution,
  phraseCautionCourte,
  sousBoutonCaution,
  libellesEtapeCaution,
  modeCautionConnu,
};
